import {useMemo, useState} from 'react'
import {Card, Form, Row, Col, Spinner, Button} from 'react-bootstrap'
import {useQuery} from '@apollo/client'
import axios from 'axios'
import {toast} from 'react-toastify'
import {TWILIO_NUMBERS_USAGE_REPORT} from '../../gql/queries/twilioBilling'
import {MessageDescriptor, useIntl} from 'react-intl'
import {useTableSort} from '../../helpers/useTableSort'
import {BILLING_MESSAGES as M, EM_DASH, POLICY_DIAL} from './billingMessages'
import {URL as BACKEND_URL} from '../../gql/client'
import {STORAGE_NAMES} from '../../helpers/consts'
import {US_STATES} from '../../helpers/catalogs'

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem(STORAGE_NAMES.TOKEN)}`,
  'Content-Type': 'application/json',
})

const money = (n: number | null | undefined) =>
  n == null ? '—' : `$${Number(n).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`

const formatE164 = (e164: string) => {
  const m = (e164 || '').match(/^\+1(\d{3})(\d{3})(\d{4})$/)
  return m ? `+1 (${m[1]}) ${m[2]}-${m[3]}` : e164
}

// 2-letter code → full state name (same rendering as the Inventory numbers tab).
const STATE_NAME: Record<string, string> = Object.fromEntries(
  US_STATES.map((s: any) => [s.code || s.value, s.name || s.label])
)
const stateName = (code: string) => (code ? STATE_NAME[code] || code : '—')

const CLASS_BADGE: Record<string, string> = {
  pool: 'badge-light-primary',
  policydial: 'badge-light-success',
  messaging: 'badge-light-info',
  unclassified: 'badge-light-warning',
}
const CLASS_LABEL: Record<string, MessageDescriptor> = {
  pool: M.classPool,
  messaging: M.classMessaging,
  unclassified: M.classUnclassified,
}

const PAGE_SIZE = 25

// Build the last 6 month options (value = "YYYY-M").
const monthOptions = (() => {
  const out: {value: string; label: string}[] = []
  const now = new Date()
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    out.push({
      value: `${d.getFullYear()}-${d.getMonth() + 1}`,
      label: d.toLocaleString('en-US', {month: 'long', year: 'numeric'}),
    })
  }
  return out
})()

// Admin: per-number voice consumption + Twilio cost for a month (all owned numbers).
export const UsageByNumberTab = () => {
  const intl = useIntl()
  const [period, setPeriod] = useState(monthOptions[0].value)
  const [year, month] = period.split('-').map(Number)
  const [rebuilding, setRebuilding] = useState(false)

  const rebuildUsage = async () => {
    setRebuilding(true)
    try {
      const res = await axios.post<{queued: number}>(
        `${BACKEND_URL}/api/telephony/supervisor/usage-rebuild?year=${year}&month=${month}`,
        {},
        {headers: authHeaders()}
      )
      toast.success(intl.formatMessage(M.toastQueued, {count: res.data?.queued ?? 0}))
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || intl.formatMessage(M.errRebuild))
    } finally {
      setRebuilding(false)
    }
  }
  const {data, loading} = useQuery(TWILIO_NUMBERS_USAGE_REPORT, {
    variables: {year, month},
    fetchPolicy: 'cache-and-network',
  })
  const report = data?.twilioNumbersUsageReport

  const [classFilter, setClassFilter] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const {onSort, sortRows, arrow} = useTableSort<any>('cost', 'desc')

  const rows = useMemo(() => {
    let r: any[] = report?.numbers ?? []
    if (classFilter) r = r.filter((x) => x.classification === classFilter)
    if (search.trim()) {
      const q = search.replace(/[^0-9]/g, '')
      r = r.filter((x) => (x.phone_number || '').replace(/[^0-9]/g, '').includes(q))
    }
    return sortRows(r)
  }, [report, classFilter, search, sortRows])

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const current = Math.min(page, pageCount)
  const pageRows = rows.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE)

  return (
    <div className='mt-5'>
      <div className='mb-5 d-flex justify-content-between align-items-start flex-wrap gap-3'>
        <div>
          <h2 className='mb-1'>{intl.formatMessage(M.usageTitle)}</h2>
          <span className='text-muted'>{intl.formatMessage(M.usageSubtitle)}</span>
          <div className='text-muted fs-8 mt-1'>
            {intl.formatMessage(M.usageHint, {action: intl.formatMessage(M.rebuildData)})}
          </div>
        </div>
        <div className='d-flex gap-2 align-items-start'>
          <Form.Select
            style={{maxWidth: 220}}
            value={period}
            onChange={(e: any) => {
              setPeriod(e.target.value)
              setPage(1)
            }}
          >
            {monthOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
          <Button
            variant='light'
            onClick={rebuildUsage}
            disabled={rebuilding}
            title={intl.formatMessage(M.rebuildTitle)}
          >
            {intl.formatMessage(rebuilding ? M.queuing : M.rebuildData)}
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <Row className='g-4 mb-5'>
        {[
          {
            label: intl.formatMessage(M.cardNumbersWithActivity),
            value: report?.count ?? EM_DASH,
          },
          {label: intl.formatMessage(M.cardTotalCalls), value: report?.total_calls ?? EM_DASH},
          {label: intl.formatMessage(M.cardTotalMinutes), value: report ? report.total_min : EM_DASH},
          {
            label: intl.formatMessage(M.cardTotalTwilioCost),
            value: report ? money(report.total_cost) : EM_DASH,
          },
        ].map((c) => (
          <Col md={3} sm={6} key={c.label}>
            <Card className='h-100'>
              <Card.Body className='py-4'>
                <div className='fs-2 fw-bolder'>{c.value}</div>
                <div className='text-muted fs-7'>{c.label}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Card>
        <Card.Header className='d-flex align-items-center justify-content-between flex-wrap gap-3'>
          <Card.Title className='mb-0'>{intl.formatMessage(M.byNumber)}</Card.Title>
          <div className='d-flex gap-2 flex-wrap'>
            <Form.Select
              size='sm'
              style={{maxWidth: 160}}
              value={classFilter}
              onChange={(e: any) => {
                setClassFilter(e.target.value)
                setPage(1)
              }}
            >
              <option value=''>{intl.formatMessage(M.allTypes)}</option>
              <option value='pool'>{intl.formatMessage(M.classPool)}</option>
              <option value='policydial'>{POLICY_DIAL}</option>
              <option value='messaging'>{intl.formatMessage(M.classMessaging)}</option>
              <option value='unclassified'>{intl.formatMessage(M.classUnclassified)}</option>
            </Form.Select>
            <Form.Control
              size='sm'
              style={{maxWidth: 200}}
              placeholder={intl.formatMessage(M.searchNumber)}
              value={search}
              onChange={(e: any) => {
                setSearch(e.target.value)
                setPage(1)
              }}
            />
          </div>
        </Card.Header>
        <Card.Body className='p-0'>
          {loading && !report ? (
            <div className='text-center py-10'>
              <Spinner animation='border' size='sm' />
            </div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-row-dashed align-middle gy-2 mb-0'>
                <thead>
                  <tr className='fw-bold text-muted' style={{cursor: 'pointer'}}>
                    <th className='ps-6' onClick={() => onSort('phone_number')}>{intl.formatMessage(M.number)}{arrow('phone_number')}</th>
                    <th onClick={() => onSort('classification')}>{intl.formatMessage(M.type)}{arrow('classification')}</th>
                    <th onClick={() => onSort('state')}>{intl.formatMessage(M.state)}{arrow('state')}</th>
                    <th onClick={() => onSort('owner')}>{intl.formatMessage(M.owner)}{arrow('owner')}</th>
                    <th className='text-end' onClick={() => onSort('inbound_calls')}>{intl.formatMessage(M.colIn)}{arrow('inbound_calls')}</th>
                    <th className='text-end' onClick={() => onSort('outbound_calls')}>{intl.formatMessage(M.colOut)}{arrow('outbound_calls')}</th>
                    <th className='text-end' onClick={() => onSort('total_calls')}>{intl.formatMessage(M.total)}{arrow('total_calls')}</th>
                    <th className='text-end' onClick={() => onSort('total_min')}>{intl.formatMessage(M.colMinutes)}{arrow('total_min')}</th>
                    <th className='text-end pe-6' onClick={() => onSort('cost')}>{intl.formatMessage(M.cost)}{arrow('cost')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r: any) => (
                    <tr key={r.phone_number}>
                      <td className='ps-6'>
                        <div className='fw-bold'>{formatE164(r.phone_number)}</div>
                        {r.friendly_name && r.friendly_name !== r.phone_number && (
                          <div className='text-muted fs-8'>{r.friendly_name}</div>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${CLASS_BADGE[r.classification] || 'badge-light'}`}>
                          {CLASS_LABEL[r.classification]
                            ? intl.formatMessage(CLASS_LABEL[r.classification])
                            : r.classification === 'policydial'
                            ? POLICY_DIAL
                            : r.classification}
                        </span>
                      </td>
                      <td>{stateName(r.state)}</td>
                      <td className='text-muted'>{r.owner || EM_DASH}</td>
                      <td className='text-end'>{r.inbound_calls}</td>
                      <td className='text-end'>{r.outbound_calls}</td>
                      <td className='text-end fw-semibold'>{r.total_calls}</td>
                      <td className='text-end text-muted'>{r.total_min}</td>
                      <td className='text-end fw-semibold pe-6'>{money(r.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length === 0 && (
                <p className='text-muted mb-0 p-6'>{intl.formatMessage(M.usageEmpty)}</p>
              )}
            </div>
          )}
        </Card.Body>
        {pageCount > 1 && (
          <Card.Footer className='d-flex align-items-center justify-content-between'>
            <span className='text-muted fs-7'>
              {intl.formatMessage(M.pageSummary, {
                count: rows.length,
                current,
                total: pageCount,
              })}
            </span>
            <div className='d-flex gap-2'>
              <button
                className='btn btn-sm btn-light'
                disabled={current <= 1}
                onClick={() => setPage(current - 1)}
              >
                {intl.formatMessage(M.prev)}
              </button>
              <button
                className='btn btn-sm btn-light'
                disabled={current >= pageCount}
                onClick={() => setPage(current + 1)}
              >
                {intl.formatMessage(M.next)}
              </button>
            </div>
          </Card.Footer>
        )}
      </Card>
    </div>
  )
}
